import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiStar } from 'react-icons/hi';

const ResultsSectionV2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const results = [
    { text: 'Greater clarity and confidence', color: 'from-[#5B8FB9] to-[#7CB98F]' },
    { text: 'Reduced stress and burnout', color: 'from-[#D4A5B5] to-[#5B8FB9]' },
    { text: 'Stronger leadership presence', color: 'from-[#7CB98F] to-[#D4A5B5]' },
    { text: 'Improved team dynamics', color: 'from-[#5B8FB9] to-[#D4A5B5]' },
    { text: 'Sustainable performance growth', color: 'from-[#7CB98F] to-[#5B8FB9]' },
  ];

  const testimonials = [
    {
      quote: "Working with Susana transformed how I lead my team. I went from constantly firefighting to strategic thinking. The clarity I gained has been invaluable.",
      author: "Maria K.",
      role: "VP of Operations, Tech Startup",
      gradient: 'from-[#5B8FB9]/10 to-[#7CB98F]/10',
      starColor: '#5B8FB9',
    },
    {
      quote: "Susana's approach helped us reduce team burnout by 40% while actually improving our output. She truly understands the balance between performance and well-being.",
      author: "David L.",
      role: "CEO, Marketing Agency",
      gradient: 'from-[#7CB98F]/10 to-[#D4A5B5]/10',
      starColor: '#7CB98F',
    },
    {
      quote: "The leadership mentorship was exactly what I needed. I now have tools and frameworks I use daily. My confidence in decision-making has completely changed.",
      author: "Ana S.",
      role: "Director of People, Healthcare",
      gradient: 'from-[#D4A5B5]/10 to-[#5B8FB9]/10',
      starColor: '#D4A5B5',
    },
  ];

  return (
    <section
      id="results"
      ref={ref}
      data-testid="results-section-v2"
      className="py-24 md:py-32 bg-gradient-to-br from-[#F8FCFA] via-[#FDF8FA] to-[#F5F9FC] relative overflow-hidden"
    >
      {/* Animated decorative elements */}
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        className="absolute top-10 right-10 w-32 h-32 border-2 border-dashed border-[#5B8FB9]/20 rounded-full" 
      />
      <motion.div 
        animate={{ rotate: -360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-10 left-10 w-48 h-48 border-2 border-dashed border-[#D4A5B5]/20 rounded-full" 
      />
      <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-[#7CB98F]/10 rounded-full blur-xl -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#D4A5B5]/10 to-[#5B8FB9]/10 text-[#D4A5B5] text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-[#D4A5B5]/20">
            Results
          </span>
          <h2 
            className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="results-headline-v2"
          >
            What Clients Experience
          </h2>
        </motion.div>

        {/* Results Pills - Colorful */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 md:gap-4 mb-16"
        >
          {results.map((result, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05, y: -2 }}
              className={`flex items-center gap-2 px-5 py-3 bg-gradient-to-r ${result.color} text-white rounded-full shadow-md`}
              data-testid={`result-item-v2-${index + 1}`}
            >
              <span className="w-2 h-2 bg-white rounded-full" />
              <span className="text-sm font-medium">{result.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.15 }}
              whileHover={{ y: -5 }}
              className={`bg-gradient-to-br ${testimonial.gradient} backdrop-blur-sm p-8 rounded-[2rem] border border-white/50 shadow-sm hover:shadow-lg transition-all duration-300`}
              data-testid={`testimonial-v2-${index + 1}`}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <HiStar className="w-5 h-5" style={{ color: testimonial.starColor }} />
                  </motion.div>
                ))}
              </div>

              {/* Quote */}
              <p className="text-gray-600 font-light leading-relaxed mb-6 relative">
                <span 
                  className="text-5xl absolute -top-2 -left-1 opacity-30" 
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: testimonial.starColor }}
                >"</span>
                <span className="relative z-10 pl-4">{testimonial.quote}</span>
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                  style={{ background: `linear-gradient(135deg, ${testimonial.starColor}, ${testimonial.starColor}88)` }}
                >
                  {testimonial.author.charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResultsSectionV2;
